import type { CalculationResult, ShopifyCartItem } from '../types';
import { formatCurrency } from './formatCurrency';

/**
 * Update Shopify cart UI with discount information
 * This function manipulates the DOM directly to update cart prices
 */
export function updateCartUI(
  shopifyCartItems: ShopifyCartItem[],
  calculationResult: CalculationResult | null
): void {
  if (!calculationResult) {
    return;
  }

  // Create a map of discount info by item key (base key without .001 suffix)
  const discountMap = new Map<string, {
    originalPrice: number;
    discountAmount: number;
    finalPrice: number;
    hasDiscount: boolean;
  }>();

  // Process line item discounts
  calculationResult.line_item_discounts.forEach((lineItem) => {
    const baseKey = lineItem.key;
    const discountInfo = discountMap.get(baseKey) || {
      originalPrice: 0,
      discountAmount: 0,
      finalPrice: 0,
      hasDiscount: false,
    };

    discountInfo.discountAmount = lineItem.total_discount_amount;
    discountInfo.hasDiscount = discountInfo.discountAmount > 0;
    
    // Calculate average price per unit
    const shopifyItem = shopifyCartItems.find(
      (item) => `cart_item_${item.id}` === baseKey || item.key === baseKey
    );
    if (shopifyItem) {
      discountInfo.originalPrice = shopifyItem.price / 100; // Convert cents
      discountInfo.finalPrice =
        discountInfo.originalPrice -
        discountInfo.discountAmount / shopifyItem.quantity;
    }

    discountMap.set(baseKey, discountInfo);
  });

  // Update each cart item in the DOM
  shopifyCartItems.forEach((item) => {
    const itemKey = item.key || `cart_item_${item.id}`;
    const baseKey = itemKey.split('.')[0]; // Remove .001 suffix if present
    const discountInfo = discountMap.get(baseKey);

    if (!discountInfo || !discountInfo.hasDiscount) {
      return;
    }

    // Find the cart item element
    // Common Shopify selectors (may need customization per theme)
    const itemSelector = `[data-line-item-key="${itemKey}"]`;
    let itemElement = document.querySelector(itemSelector);

    // Try alternative selectors if first doesn't work
    if (!itemElement) {
      itemElement = document.querySelector(`[data-variant-id="${item.variant_id}"]`);
    }
    if (!itemElement) {
      itemElement = document.querySelector(
        `.cart-item[data-product-id="${item.product_id}"]`
      );
    }

    if (itemElement) {
      // Update price display
      const priceElement = itemElement.querySelector('.cart-item__price');
      if (priceElement) {
        const originalPriceText = formatCurrency(discountInfo.originalPrice, { withSpace: true });
        const finalPriceText = formatCurrency(discountInfo.finalPrice, { withSpace: true });
        
        if (discountInfo.hasDiscount) {
          priceElement.innerHTML = `
            <span style="text-decoration: line-through; color: #999; margin-right: 8px;">
              ${originalPriceText}
            </span>
            <span style="color: #28a745; font-weight: bold;">
              ${finalPriceText}
            </span>
          `;
        }
      }

      // Update line total
      const totalElement = itemElement.querySelector('.cart-item__total');
      if (totalElement) {
        const lineTotal = discountInfo.finalPrice * item.quantity;
        totalElement.textContent = formatCurrency(lineTotal, { withSpace: true });
      }

      // Add discount badge
      if (!itemElement.querySelector('.intan-discount-badge') && priceElement) {
        const badge = document.createElement('div');
        badge.className = 'intan-discount-badge';
        badge.style.cssText = `
          display: inline-block;
          padding: 2px 6px;
          background-color: #28a745;
          color: white;
          border-radius: 3px;
          font-size: 10px;
          font-weight: bold;
          margin-left: 8px;
        `;
        badge.textContent = 'DISCOUNTED';
        priceElement.appendChild(badge);
      }
    }
  });

  // Update cart summary/total
  updateCartSummary(calculationResult);
}

/**
 * Update cart summary section with discount totals
 */
function updateCartSummary(result: CalculationResult): void {
  // Try to find cart summary elements
  const summarySelectors = [
    '.cart-summary',
    '.cart__summary',
    '[data-cart-summary]',
    '.cart-totals',
  ];

  let summaryElement: Element | null = null;
  for (const selector of summarySelectors) {
    summaryElement = document.querySelector(selector);
    if (summaryElement) break;
  }

  if (!summaryElement) {
    // If no summary element found, try to find subtotal element
    summaryElement = document.querySelector('.cart__subtotal') || 
                     document.querySelector('[data-cart-subtotal]');
  }

  if (summaryElement) {
    // Update subtotal if it exists
    const subtotalElement = summaryElement.querySelector('.cart__subtotal-price') ||
                           summaryElement.querySelector('[data-subtotal]');
    if (subtotalElement) {
      subtotalElement.textContent = formatCurrency(result.original_total, { withSpace: true });
    }

    // Add or update discount line
    let discountLine = summaryElement.querySelector('.intan-discount-line') as HTMLElement;
    if (!discountLine && result.total_discount > 0) {
      discountLine = document.createElement('div');
      discountLine.className = 'intan-discount-line';
      discountLine.style.cssText = `
        display: flex;
        justify-content: space-between;
        color: #28a745;
        margin: 8px 0;
        font-size: 14px;
      `;
      discountLine.innerHTML = `
        <span>Discount:</span>
        <span>- ${formatCurrency(result.total_discount, { withSpace: true })}</span>
      `;
      
      // Insert before total
      const totalElement = summaryElement.querySelector('.cart__total') ||
                          summaryElement.querySelector('[data-cart-total]');
      if (totalElement && totalElement.parentElement) {
        totalElement.parentElement.insertBefore(discountLine, totalElement);
      }
    } else if (discountLine && result.total_discount > 0) {
      discountLine.innerHTML = `
        <span>Discount:</span>
        <span>- ${formatCurrency(result.total_discount, { withSpace: true })}</span>
      `;
    } else if (discountLine && result.total_discount === 0) {
      discountLine.remove();
    }

    // Update total
    const totalElement = summaryElement.querySelector('.cart__total-price') ||
                        summaryElement.querySelector('[data-cart-total]');
    if (totalElement) {
      totalElement.textContent = formatCurrency(result.final_total, { withSpace: true });
    }
  }
}

